/* eslint-disable react/jsx-props-no-spreading */
import { OpenWithOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import useDebouncedValue from "hooks/useDebouncedValue";
import useUserPilot from "hooks/useUserPilot";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Swal from "sweetalert2";
import sampleProfileImage from "assets/images/sample.png";
import { getAPICall, postAPICall } from "lib/callApi";
import {
  ASSIGN_KPI_TO_USER,
  COPY_TEMPLATE,
  FETCH_ALL_KPI_TEMPLATES,
  FETCH_KPI_ACTIVITIES,
  USERS_WITH_INPUT_KPI_PERMISSION,
  FETCH_SINGLE_KPI_TEMPLATE,
} from "lib/constants";
import "styles/kpis.scss";
import KPITile from "../components/KPITile";
import TemplateDetailModal from "../components/TemplateDetailModal";
import CreateKPITemplateModal from "../components/CreateKPITemplateModal";

function Templates() {
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const Userpilot = useUserPilot();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [inEditMode, setInEditMode] = useState(false);
  const [templateDetail, setTemplateDetail] = useState({});
  const [detailId, setDetailId] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const userSearchTermDebouncedValue = useDebouncedValue(userSearchTerm, 500);
  const [alreadyAssignedUsers, setAlreadyAssignedUsers] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [templateData, setTemplateData] = useState([]);
  const [filteredTemplateData, setFilteredTemplateData] = useState([]);
  const [kpiActivities, setKpiActivities] = useState([]);
  const [isTemplateFetching, setIsTemplateFetching] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [checkboxFilters, setCheckboxFilters] = useState({
    showActive: true,
    showUnassigned: true,
    showAlysio: true,
  });

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobileDisplay ? "90%" : 750,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
    borderRadius: "0.5rem",
    outline: "none",
  };

  const getTemplates = async () => {
    setIsTemplateFetching(true);
    try {
      const res = await getAPICall(FETCH_ALL_KPI_TEMPLATES, true);
      if (res?.data?.success) {
        setTemplateData(res?.data?.data?.templates || []);
        setAlreadyAssignedUsers(res?.data?.data?.assignedUsers);
      } else if (res?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: res?.response?.data?.message || res.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.data?.message || error.response.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsTemplateFetching(false);
    }
  };

  const handleDetailModalClose = (_, __, retriveNewData) => {
    setInEditMode(false);
    setShowDetailModal(false);
    if (retriveNewData) {
      getTemplates();
    }
  };

  const handleOpenCreateModal = async () => {
    setTemplateDetail(undefined);
    setIsCreateModalOpen(true);
  };

  const handleOpenCopyModal = async (templateId) => {
    const result = await getAPICall(`${FETCH_SINGLE_KPI_TEMPLATE}/${templateId}`, true);
    if (result?.data?.success) {
      setTemplateDetail({
        template: result.data.data.template,
        users: result.data.data.userAssignedToTemplate,
        activitiesWithPoints: result.data.data.activitiesWithPoints,
      });
      setIsCreateModalOpen(true);
    }
  };

  const showNotification = (type, message) => {
    enqueueSnackbar(
      JSON.stringify({
        icon: type,
        title: type === "success" ? "Success" : "Error",
        text: message,
      }),
      {
        variant: "custom",
      }
    );
  };

  useEffect(() => {
    const filteredTemplates = templateData.filter((template) => {
      const nameMatch = template.name.toLowerCase().includes(searchFilter.toLowerCase());
      const userMatch = template.Users.some((user) =>
        user.name.toLowerCase().includes(searchFilter.toLowerCase())
      );

      return nameMatch || userMatch;
    });
    const filteredCheckboxTemplates = filteredTemplates.filter((template) => {
      if (!checkboxFilters.showActive && template.Users.length > 0) {
        return false;
      }
      if (!checkboxFilters.showUnassigned && template.Users.length === 0) {
        return false;
      }
      if (!checkboxFilters.showAlysio && template.status === "alysio_template") {
        return false;
      }
      return true;
    });
    setFilteredTemplateData(filteredCheckboxTemplates);
  }, [templateData, searchFilter, checkboxFilters]);

  const getKpiActivities = () => {
    getAPICall(FETCH_KPI_ACTIVITIES, true).then((res) => {
      setKpiActivities(res.data.data);
    });
  };
  const getCompanyUsers = (payload) => {
    getAPICall(USERS_WITH_INPUT_KPI_PERMISSION, true, payload).then((res) => {
      setCompanyUsers(res.data.data);
    });
  };

  const refreshKpiTemplates = async () => {
    getTemplates();
    getKpiActivities();
  };

  useEffect(() => {
    refreshKpiTemplates();
  }, []);

  useEffect(() => {
    setUnassignedUsers(
      companyUsers.filter((user) => !alreadyAssignedUsers.includes(user?.userId) || user.disabled)
    );
  }, [companyUsers, alreadyAssignedUsers]);

  useEffect(() => {
    getCompanyUsers({ searchBy: userSearchTerm });
  }, [userSearchTermDebouncedValue]);

  const handleCheckboxFiltersChanged = (event) => {
    const newFilters = { ...checkboxFilters };
    newFilters[event.target.value] = event.target.checked;
    setCheckboxFilters(newFilters);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    const matchedTemplate = filteredTemplateData?.find(
      (template) => template?.id?.toString() === destination?.droppableId?.toString()
    );
    const matchedUser = companyUsers?.find(
      (user) => user?.userId?.toString() === draggableId?.toString()
    );

    if (!matchedTemplate || !matchedUser) return;
    setIsTemplateFetching(true);
    postAPICall(ASSIGN_KPI_TO_USER, true, {
      templateId: Number(destination?.droppableId),
      newAddedUsers: [Number(draggableId)],
      removedUsers: [],
    })
      .then(() => {
        getCompanyUsers({ searchBy: userSearchTerm });
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Template assigned succesfully.",
          }),
          {
            variant: "custom",
          }
        );
        handleDetailModalClose(undefined, undefined, true);
      })
      .catch(() => {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not assign the template to user",
          }),
          {
            variant: "custom",
          }
        );
      })
      .finally(() => setIsTemplateFetching(false));
  };

  const handleMakeCopy = (data) => {
    handleDetailModalClose();
    Swal.fire({
      // title: `Are you sure you want to archive selected KPI's?`,
      input: "text",
      inputPlaceholder: `Copy of Alysio ${data.name} Template`,
      inputValidator: (inputValue) => !inputValue && "Template name is required",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "MAKE A COPY",
      reverseButtons: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await postAPICall(`${COPY_TEMPLATE}/${data.id}`, true, {
            name: result.value,
          });
          if (response?.data?.success) {
            Userpilot.track("KPI Template Copied", {
              kpiTemplateName: data.name,
            });
            enqueueSnackbar(
              JSON.stringify({
                icon: "success",
                title: "Success",
                text: "Template copied successfully!",
              }),
              {
                variant: "custom",
              }
            );
            setInEditMode(true);
            setDetailId(response.data.data.template.id);
            setShowDetailModal(true);
          } else {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "error",
                text: "Could not create template, Please try again!",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  const handleOpenKpiCardDetails = (id) => {
    setDetailId(id);
    setShowDetailModal(true);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <MDBox
        sx={{
          "& .swal2-container": {
            zIndex: "14000 !important",
          },
        }}
      >
        {isTemplateFetching ? (
          <Stack alignItems="center">
            <CircularProgress
              sx={{
                position: "fixed",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
              }}
              color="info"
              size="10vh"
            />
          </Stack>
        ) : (
          ""
        )}
        <MDBox>
          <Stack
            direction={isMobileDisplay ? "column" : "row"}
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" flexDirection={isMobileDisplay ? "column" : "row"}>
              <MDInput
                label="Search Template or User"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{ mr: "1rem", width: "200px" }}
              />
              <FormGroup
                aria-label="position"
                sx={{ flexDirection: isMobileDisplay ? "column" : "row" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      value="showActive"
                      checked={checkboxFilters.showActive}
                      onChange={handleCheckboxFiltersChanged}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.85rem" }}>Show Active</Typography>}
                  sx={{ display: "flex" }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      value="showUnassigned"
                      checked={checkboxFilters.showUnassigned}
                      onChange={handleCheckboxFiltersChanged}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.85rem" }}>Show Unassigned</Typography>}
                  sx={{ display: "flex" }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      value="showAlysio"
                      checked={checkboxFilters.showAlysio}
                      onChange={handleCheckboxFiltersChanged}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.85rem" }}>Show Alysio Templates</Typography>
                  }
                  sx={{ display: "flex" }}
                />
              </FormGroup>
            </Box>
            <Box>
              <MDButton type="submit" color="primaryRed" onClick={handleOpenCreateModal}>
                + Add new template
              </MDButton>
            </Box>
          </Stack>

          <Grid
            container
            xs={12}
            spacing={0}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid item xs={12} sm={12} lg={8} md={8}>
              <Grid
                container
                xs={12}
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                {filteredTemplateData.map((template) => (
                  <KPITile
                    key={template.id}
                    isMobileDisplay={isMobileDisplay}
                    template={template}
                    isDragging={isDragging}
                    handleOpenKpiCardDetails={handleOpenKpiCardDetails}
                    handleOpenCopyModal={handleOpenCopyModal}
                  />
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} lg={4} md={4}>
              <Typography sx={{ fontSize: "1.5rem", pl: "0.5rem" }}>Unassigned Users</Typography>
              <Card sx={{ display: "flex", justifyContent: "center", borderRadius: "0.5rem" }}>
                <MDBox
                  sx={{
                    p: "0.5rem 1rem",
                    "& .MuiInputLabel-formControl": { lineHeight: "15px" },
                  }}
                >
                  <TextField
                    label="Search by name, role, or team"
                    variant="standard"
                    sx={{
                      width: "100%",
                      "& .MuiInputLabel-root": {
                        lineHeight: "normal",
                        top: "-2px",
                      },
                    }}
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                </MDBox>
                {unassignedUsers.length > 0 ? (
                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                      maxHeight: "55vh",
                      overflow: "auto",
                      borderRadius: "0.5rem",
                    }}
                  >
                    {unassignedUsers.map((user, index) => (
                      <React.Fragment key={user.userId}>
                        <Droppable
                          droppableId={`list-${String(user.userId)}`}
                          isDropDisabled={!!user.disabled}
                        >
                          {(providedOuter) => (
                            <div ref={providedOuter.innerRef} {...providedOuter.droppableProps}>
                              <Draggable
                                key={String(user.userId)}
                                draggableId={String(user.userId)}
                                index={index}
                                isDragDisabled={!!user.disabled}
                              >
                                {(providedInner) => (
                                  <div
                                    ref={providedInner?.innerRef}
                                    {...providedInner?.draggableProps}
                                    {...providedInner?.dragHandleProps}
                                  >
                                    <ListItem
                                      alignItems="flex-start"
                                      sx={{ padding: ".4rem 1rem" }}
                                    >
                                      <ListItemAvatar>
                                        <Avatar
                                          variant="circular"
                                          alt={user?.name?.toUpperCase()}
                                          src={user?.profilePicture || sampleProfileImage}
                                        />
                                      </ListItemAvatar>
                                      <ListItemText
                                        primary={
                                          <Typography
                                            sx={{ display: "block", textTransform: "capitalize" }}
                                            component="h5"
                                            variant="body2"
                                            color="text.primary"
                                          >
                                            {user?.name} {user.disabled ? " - Inactive" : ""}
                                          </Typography>
                                        }
                                        secondary={
                                          <>
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  display: "inline",
                                                  textTransform: "capitalize",
                                                }}
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                {user?.role || "-"}
                                              </Typography>
                                              {!user.disabled ? (
                                                <OpenWithOutlined
                                                  fontSize="small"
                                                  style={{ cursor: "pointer" }}
                                                />
                                              ) : null}
                                            </div>
                                            <Typography
                                              sx={{
                                                display: "block",
                                                textTransform: "capitalize",
                                              }}
                                              component="span"
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {user?.teamName}
                                            </Typography>
                                          </>
                                        }
                                      />
                                    </ListItem>
                                  </div>
                                )}
                              </Draggable>
                              {index < unassignedUsers.length - 1 && (
                                <Divider
                                  variant="inset"
                                  component="li"
                                  sx={{ margin: ".2rem 0rem" }}
                                />
                              )}
                              {providedOuter?.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography sx={{ fontSize: "0.9rem", color: "#888", p: "1rem 1rem" }}>
                    There are no unassigned users
                  </Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {showDetailModal && (
          <TemplateDetailModal
            handleDetailModalClose={handleDetailModalClose}
            showDetailModal={showDetailModal}
            alreadyAssignedUsers={alreadyAssignedUsers}
            id={detailId}
            setShowDetailModal={setShowDetailModal}
            kpiActivities={kpiActivities}
            getKpiActivities={getKpiActivities}
            handleMakeCopy={handleMakeCopy}
            inEditMode={inEditMode}
            isMobileDisplay={isMobileDisplay}
          />
        )}

        {isCreateModalOpen && (
          <CreateKPITemplateModal
            open={isCreateModalOpen}
            handleClose={() => setIsCreateModalOpen((prev) => !prev)}
            style={modalStyle}
            templateDetail={templateDetail}
            kpiActivities={kpiActivities}
            refreshKpiTemplates={refreshKpiTemplates}
            unassignedUsers={unassignedUsers}
            showNotification={showNotification}
            isMobileDisplay={isMobileDisplay}
          />
        )}
      </MDBox>
    </DragDropContext>
  );
}

export default Templates;
